import { CustomInputField } from "@/components/chat/input/CustomInputField";
import { AssistantMessageBubble } from "@/components/chat/message/AssistantMessageBubble";
import { LoadingAssistantMessageBubble } from "@/components/chat/message/LoadingAssistantMessageBubble";
import { UserMessageBubble } from "@/components/chat/message/UserMessageBubble";
import { SearchButton } from "@/components/common/button/SearchButton";
import { ThreadFeedbackArea } from "@/components/feedback/ThreadFeedbackArea";
import { SearchDrawer } from "@/components/search/SearchDrawer";
import { useFeedbacks } from "@/hooks/api/feedbacks/useFeedbacks";
import { useSendFeedbacks } from "@/hooks/api/feedbacks/useSendFeedback";
import { useMessages } from "@/hooks/api/messages/useMessages";
import { useSendMessage } from "@/hooks/api/messages/useSendMessage";
import { useServices } from "@/hooks/api/services/useServices";
import { useThreads } from "@/hooks/api/threads/useThreads";
import { Feedback } from "@/models/Feedback";
import { Choice, MessageRole, Messages, MessageType, UserMessage } from "@/models/Message";
import {
  makeStyles,
  Text,
  Toast,
  Toaster,
  ToastTitle,
  tokens,
  useId,
  useToastController,
} from "@fluentui/react-components";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { ulid } from "ulid";

export const ChatPage: React.FC = () => {
  // 表示対象のスレッドIDを取得
  const { threadId } = useParams<{ threadId: string; }>();
  if (!threadId) throw new Error("threadId is required");

  // 関連するデータ用のフックを取得
  const { mutate: updateThreads } = useThreads();

  const { data: services } = useServices();
  const { data: messages, mutate: updateMessages } = useMessages(threadId);
  const { trigger: sendMessage, isMutating } = useSendMessage(threadId);
  const { data: feedbacks, mutate: updateFeedbacks } = useFeedbacks(threadId);
  const { trigger: sendFeedback } = useSendFeedbacks(threadId);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  // スレッドの最初のレスポンスが来るまでローディング状態にするためのフラグ
  const [waitingFirstResponse, setWaitingFirstResponse] = useState(false);

  // メッセージ送信処理
  const handleSendMessage = async (message: string, recipientId: string | undefined) => {
    const recipientToSend = recipientId != undefined ? recipientId : messages!.lastRecipientService();
    if (!recipientToSend) {
      throw new Error("Recipient ID is null");
    }

    const newMessage = new UserMessage({
      id: ulid(),
      threadId: threadId,
      sender: "",
      recipient: recipientToSend,
      content: message,
    });

    const newMessages = new Messages([...messages!, newMessage]);
    await updateMessages(
      newMessages,
      { revalidate: false }, // 再取得されると消えてしまうのでOff
    );
    await sendMessage(newMessage);
    updateThreads();
    updateMessages();
  };

  // 最初のレスポンスが来るまで、ローディング状態にする
  useEffect(() => {
    if (messages?.length == 1 && messages[0].role === MessageRole.user) {
      setWaitingFirstResponse(true);
    } else {
      setWaitingFirstResponse(false);
    }
  }, [messages]);

  // 自動的に下までスクロールするための処理
  const bottomRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isMutating]); // Dependencies for scrolling

  const onSearchResultClick = (message_id: string) => {
    const element = document.getElementById(message_id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
      });
    }
  };

  const handleRatingChange = async (score: number) => {
    if (!feedbacks || !threadId) return;

    const newFeedback = new Feedback(
      ulid(), // Generate a new ID for the feedback
      undefined, // Empty messageId for thread feedback
      score,
      "", // Empty comment
    );

    const updatedFeedbacks = feedbacks.update(newFeedback);

    updateFeedbacks(updatedFeedbacks, { revalidate: false });

    await sendFeedback(newFeedback);
  };

  const handleMessageFeedback = async (messageId: string, currentFeedbackId: string | undefined, score: number) => {
    if (!feedbacks) return;

    const newFeedback = new Feedback(
      ulid(),
      messageId,
      score,
      "",
    );

    const updatedFeedbacks = feedbacks.update(newFeedback);

    updateFeedbacks(updatedFeedbacks, { revalidate: false });

    await sendFeedback(newFeedback);
  };

  const handleFeedbackComment = async (comment: string) => {
    if (!feedbacks || !threadId) return;

    const existingFeedback = feedbacks.threadFeedback();
    if (!existingFeedback) return;

    const newFeedback = new Feedback(
      existingFeedback.id,
      existingFeedback.messageId,
      existingFeedback.score,
      comment,
    );

    const updatedFeedbacks = feedbacks.update(newFeedback);

    updateFeedbacks(updatedFeedbacks, { revalidate: false });

    await sendFeedback(newFeedback);
  };

  const handleMessageFeedbackComment = async (messageId: string, comment: string) => {
    if (!feedbacks) return;

    const existingFeedback = feedbacks.getForMessage(messageId);
    if (!existingFeedback) return;

    const newFeedback = new Feedback(
      existingFeedback.id,
      existingFeedback.messageId,
      existingFeedback.score, // Keep the original score
      comment,
    );

    const updatedFeedbacks = feedbacks.update(newFeedback);

    updateFeedbacks(updatedFeedbacks, { revalidate: false });

    await sendFeedback(newFeedback);
  };

  const handleChoiceClick = async (choice: Choice) => {
    const recipientToSend = messages!.lastRecipientService();
    if (!recipientToSend) {
      throw new Error("Recipient ID is null");
    }

    const newMessage = new UserMessage({
      id: ulid(),
      threadId: threadId,
      sender: "",
      recipient: recipientToSend,
      content: choice.value,
      type: MessageType.CHOICE,
    });

    const newMessages = new Messages([...messages!, newMessage]);
    await updateMessages(
      newMessages,
      { revalidate: false },
    );
    await sendMessage(newMessage);
    updateThreads();
    updateMessages();
  };

  const toasterId = useId("toaster");
  const { dispatchToast } = useToastController(toasterId);
  const onCopied = () => {
    dispatchToast(
      <Toast>
        <ToastTitle>クリップボードにコピーしました</ToastTitle>
      </Toast>,
      { intent: "info" },
    );
  };

  // 以下表示部分
  const classes = useStyles();
  let lastMentiondService = "";
  return (
    <div className={classes.container}>
      <div className={classes.chatArea}>
        <div className={classes.viewArea}>
          <div className={classes.messageWrapper}>
            {messages!.choiceReflected().map((message) => {
              if (message.role === "user") {
                // 送信先が変更された場合、@メンションを表示する
                const recipientChanged = message.recipient !== lastMentiondService;
                lastMentiondService = message.recipient;
                return (
                  <div id={message.id} key={message.id}>
                    <UserMessageBubble message={message} recipientChanged={recipientChanged} />
                  </div>
                );
              } else {
                const messageFeedback = feedbacks?.getForMessage(message.id);

                return (
                  <div id={message.id} key={message.id}>
                    <AssistantMessageBubble
                      message={message}
                      feedbackScore={messageFeedback?.score}
                      feedbackComment={messageFeedback?.comment}
                      onFeedbackSelected={(score) => handleMessageFeedback(message.id, messageFeedback?.id, score)}
                      onFeedbackCommentSubmit={(comment) => handleMessageFeedbackComment(message.id, comment)}
                      onChoiceClick={handleChoiceClick}
                      onCopied={onCopied}
                    />
                  </div>
                );
              }
            })}
            {(isMutating || waitingFirstResponse) && <LoadingAssistantMessageBubble />}
          </div>
          {/* Feedback area */}
          <div className={classes.ratingArea}>
            <ThreadFeedbackArea
              score={feedbacks?.threadFeedback()?.score} // Pass score if feedback exists, otherwise default (0) will be used
              onScoreSet={handleRatingChange}
              onCommentSubmit={(comment) => handleFeedbackComment(comment)}
              messages={messages}
              feedbacks={feedbacks}
              isMutating={isMutating}
              waitingFirstResponse={waitingFirstResponse}
              onRateChanged={(score) => {
                console.log(`Rating changed to: ${score}`);
              }}
            >
            </ThreadFeedbackArea>
          </div>
          <div ref={bottomRef} />
        </div>
        <div className={classes.inputArea}>
          <CustomInputField
            services={services}
            onSendMessage={handleSendMessage}
            defaultRecipientId={messages?.lastRecipientService()}
            disabled={isMutating || waitingFirstResponse}
          />
          <Text className={classes.disclaimerText}>
            回答は必ずしも正しいとは限りません。重要な情報は引用元を確認するようにしてください。
          </Text>
        </div>
      </div>

      <SearchButton onClick={() => setIsDrawerOpen(true)} />
      <SearchDrawer
        isDrawerOpen={isDrawerOpen}
        setIsDrawerOpen={setIsDrawerOpen}
        onResultClick={onSearchResultClick}
      />
      <Toaster
        toasterId={toasterId}
      />
    </div>
  );
};

const useStyles = makeStyles({
  container: {
    display: "flex",
    background: tokens.colorNeutralBackground1,
    height: "100%",
    position: "relative",
  },
  chatArea: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    height: "100%",
  },
  viewArea: {
    background: tokens.colorNeutralBackground1,
    height: "100%",
    flex: 1,
    overflowY: "auto",
    boxSizing: "content-box",
    padding: `${tokens.spacingVerticalXXL} ${tokens.spacingHorizontalXXXL}`,
  },
  messageWrapper: {
    background: tokens.colorNeutralBackground1,
    margin: `0 ${tokens.spacingHorizontalXXXL}`,
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXXL,
  },
  ratingArea: {
    background: tokens.colorNeutralBackground1,
    width: "100%",
    height: "140px",
    padding: `${tokens.spacingHorizontalXXL} 0`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  inputArea: {
    background: tokens.colorNeutralBackground1,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalXL}`,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
  },
  disclaimerText: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
    textAlign: "center",
    lineHeight: tokens.lineHeightBase300,
  },
});
